import type {SyntheticEvent} from 'react'
import React, {useCallback, useMemo} from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import type {Product, RootAndListContext, RootLoaderResponse, SlugApiResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {CheckedYesIcon} from '~/icons/checked-yes-icon'
import {CheckedNoIcon} from '~/icons/checked-no-icon'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {useReportWidgetSeen, useReportWidgetClick, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'
import {objectToQueryString} from '~/utils/commons'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {DiscountBadgeReorganized} from '../badges/discount-badge-reorganized'

const CompareTopPicks = ({products}: {products: Product[]}) => {
  const {language, domain, locale, trackAnalytics, featureFlags} = useRouteLoaderData('root') as RootLoaderResponse
  const {
    dataResponse: {productsCategory} = {} as SlugApiResponse,
    clickId,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const {styles} = getDomainConfig(domain, locale)

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'compare-top-picks',
    widget_place: 'bottom',
  })

  const reportWidgetClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickoutCustom()

  const formatter = new Intl.NumberFormat(language, {})
  const productsToDisplay = products.slice(0, 3)
  const columns = productsToDisplay.length
  const allKeySpecNames = useMemo(
    () =>
      Array.from(
        new Set(
          products.flatMap(
            (product) => product.keySpecs?.map((keySpec) => (keySpec.name && keySpec.value ? keySpec.name : [])) || [],
          ),
        ),
      ),
    [products],
  )
  const displayCompareTopPicks = locale?.toLowerCase() === 'de'

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = useMemo(
    () =>
      objectToQueryString({
        session_id: trackAnalytics?.session_id,
        ...trackAnalytics?.pageLoadData,
        page_id: trackAnalytics?.page_id,
        user_id: trackAnalytics?.user_id,
      }),
    [trackAnalytics],
  )

  const getAffiliateLinkWithClickId = useCallback(
    (affiliateLink: Product['affiliateLink']) => affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId),
    [clickId],
  )

  const getRedirectUrl = useCallback(
    (affiliateLink: Product['affiliateLink']) => {
      const affiliateLinkWithClickId = getAffiliateLinkWithClickId(affiliateLink)
      return shouldForceTrackAnalytics
        ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
        : affiliateLinkWithClickId
    },
    [getAffiliateLinkWithClickId, shouldForceTrackAnalytics, trackSearchParams],
  )

  const handleCardClick = useCallback(
    (
      product: Product,
      index: number,
      elementName: 'image' | 'cta' | 'title' | 'price' | 'discount' | 'keySpec' = 'cta',
    ) => {
      return (_e: SyntheticEvent) => {
        const redirectUrl = getRedirectUrl(product.affiliateLink)

        if (shouldForceTrackAnalytics) {
          setShowCookieConsentGdpr(false)
          grantAllConsent()
          submit(
            {
              'accept-gdpr': 'granted',
              redirect: 'true',
              'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
            },
            {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
          )
        }

        reportClickout({
          index: index + 1,
          item_list_id: productsCategory?.slug,
          item_name: product.name,
          item_brand: product.brand,
          item_category: productsCategory?.name,
          partner_item_id: product.asin,
          partner_item_brand: product.brand || 'unknown',
          click_element_name: `compare-top-picks-${elementName}`,
          price: product.price?.amount,
          currency: product.price?.currency,
          discount: product.coupon?.value,
          location_id: locale,
          affiliation: product.affiliateLink.type,
          target: product.affiliateLink.url,
          traffic: 'unknown',
          is_dws: product.isSellerProduct,
          dws_type: undefined,
          gclid: undefined,
          sys_id: undefined,
          click_id: product.affiliateLink.clickId,
        })

        reportWidgetClick({
          widget_name: 'compare-top-picks',
          widget_place: 'bottom',
          trigger_type: elementName,
        })

        if (typeof Storage !== 'undefined') {
          localStorage.setItem('clickTime', Date.now().toString())
          if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
        }
      }
    },
    [
      getRedirectUrl,
      shouldForceTrackAnalytics,
      setShowCookieConsentGdpr,
      submit,
      trackAnalytics,
      reportClickout,
      productsCategory?.slug,
      productsCategory?.name,
      locale,
      reportWidgetClick,
      featureFlags?.showHustonProblemPopup,
    ],
  )

  const keySpecRows = useMemo(
    () =>
      allKeySpecNames.flatMap((keySpecName) => [
        <tr key={`${keySpecName}-header`} className="header-row">
          <td colSpan={columns} className="header-cell p-3 text-start">
            {keySpecName}
          </td>
        </tr>,
        <tr key={`${keySpecName}-data`}>
          {productsToDisplay.map((product, index) => {
            const {asin, affiliateLink, brand, keySpecs} = product
            const keySpec = keySpecs?.find((spec) => spec.name === keySpecName)
            const redirectUrl = getRedirectUrl(affiliateLink)

            return (
              <td key={asin} className="cell-data relative">
                <a
                  className="absolute inset-0"
                  href={redirectUrl}
                  onClick={handleCardClick(product, index, 'keySpec')}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                ></a>
                {keySpec?.value || '--'}
              </td>
            )
          })}
        </tr>,
      ]),
    [allKeySpecNames, columns, getRedirectUrl, handleCardClick, productsToDisplay],
  )

  if (!displayCompareTopPicks) return null

  return (
    <section id="compare-top-picks" className="content-container mx-auto my-8">
      <h2 className="my-3 text-2xl font-semibold capitalize">{translations.TopPicks.compareTitle}</h2>
      <div className="overflow-x-auto rounded-3xl border border-[#DBDBDB] mobile:rounded-lg" ref={widgetRef}>
        <table className="w-full min-w-[750px] table-fixed border-collapse">
          <thead>
            <tr className="flex w-full flex-row divide-x divide-[#DBDBDB]">
              {productsToDisplay.map((product, index) => {
                const {asin, affiliateLink, image, name, brand} = product
                const redirectUrl = getRedirectUrl(affiliateLink)

                return (
                  <th
                    key={asin}
                    className="w-full shrink-0 border-b border-[#DBDBDB] px-4 py-5 text-center font-semibold"
                  >
                    <div className="flex h-full flex-col items-center justify-between space-y-2">
                      <div className="relative flex h-36 w-full items-center justify-center">
                        <a
                          className="absolute inset-0"
                          href={redirectUrl}
                          onClick={handleCardClick(product, index, 'image')}
                          aria-label={`view ${brand}`}
                          target="_blank"
                          rel="sponsored noreferrer"
                        ></a>
                        {image && <img src={image} alt={name} className="h-36 w-52 object-scale-down" loading="lazy" />}
                      </div>
                      <div className="relative line-clamp-2 text-center text-base font-light">
                        <a
                          className="absolute inset-0"
                          href={redirectUrl}
                          onClick={handleCardClick(product, index, 'title')}
                          aria-label={`view ${brand}`}
                          target="_blank"
                          rel="sponsored noreferrer"
                        ></a>
                        {name}
                      </div>
                      <a
                        href={redirectUrl}
                        onClick={handleCardClick(product, index, 'cta')}
                        aria-label={`view ${brand}`}
                        target="_blank"
                        rel="sponsored noreferrer"
                        className={clsx(
                          'mt-auto flex h-10 w-full cursor-pointer items-center justify-center rounded-3xl text-center uppercase',
                          styles.LISTPAGE_BUTTON_PRIMARY,
                          'listpage_button_primary',
                        )}
                      >
                        {translations.TopPicks.viewOnAmazon}
                      </a>
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody
            className={clsx(
              '[&_.header-cell]:bg-[#F5F5F5] [&_.header-cell]:text-base [&_.header-cell]:font-light [&_.header-cell]:text-[#363636] [&_td.cell-data]:font-light',
              '[&_td:not(.header-cell)]:flex [&_td:not(.header-cell)]:h-[74px] [&_td:not(.header-cell)]:w-full',
              '[&_td:not(.header-cell)]:shrink-0 [&_td:not(.header-cell)]:items-center [&_td:not(.header-cell)]:justify-center',
              '[&_td:not(.header-cell)]:border-b [&_td:not(.header-cell)]:border-[#DBDBDB] [&_td:not(.header-cell)]:p-3',
              '[&_td:not(.header-cell)]:text-center [&_td:not(.header-cell)]:text-base [&_td:not(.header-cell)]:text-[#363636]',
              '[&_tr:not(.header-row)]:flex [&_tr:not(.header-row)]:w-full [&_tr:not(.header-row)]:flex-row [&_tr:not(.header-row)]:divide-x [&_tr:not(.header-row)]:divide-[#DBDBDB]',
            )}
          >
            <tr className="header-row">
              <td colSpan={columns} className="header-cell p-3 text-start">
                {translations.TopPicks.deal}
              </td>
            </tr>
            <tr>
              {productsToDisplay.map((product, index) => {
                const {asin, affiliateLink, brand, discount} = product
                const redirectUrl = getRedirectUrl(affiliateLink)

                return (
                  <td key={asin} className="cell-data relative">
                    <a
                      className="absolute inset-0"
                      href={redirectUrl}
                      onClick={handleCardClick(product, index, 'discount')}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    ></a>
                    {discount ? (
                      <DiscountBadgeReorganized
                        className={clsx(
                          'mx-auto flex h-[37px] w-fit items-center justify-center rounded-full px-4 [&>p]:text-[18px]',
                          'listpage_badge_card_discount_bg listpage_badge_card_discount_text',
                          styles.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
                          styles.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
                        )}
                        discount={discount}
                      />
                    ) : (
                      '-'
                    )}
                  </td>
                )
              })}
            </tr>

            <tr className="header-row">
              <td colSpan={columns} className="header-cell p-3 text-start">
                {translations.TopPicks.chosenBy}
              </td>
            </tr>
            <tr>
              {productsToDisplay.map((product, index) => {
                const {asin, affiliateLink, brand, chosenBy} = product
                const redirectUrl = getRedirectUrl(affiliateLink)

                return (
                  <td className="cell-data relative" key={asin}>
                    <a
                      className="absolute inset-0"
                      href={redirectUrl}
                      onClick={handleCardClick(product, index, 'keySpec')}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    ></a>
                    {chosenBy ? formatter.format(chosenBy) : '--'}
                  </td>
                )
              })}
            </tr>

            <tr className="header-row">
              <td colSpan={columns} className="header-cell p-3 text-start">
                {translations.TopPicks.multipleStylesAvailable}
              </td>
            </tr>
            <tr>
              {productsToDisplay.map((product, index) => {
                const {asin, affiliateLink, brand, variationDetails} = product
                const redirectUrl = getRedirectUrl(affiliateLink)

                return (
                  <td key={asin} className="relative">
                    <a
                      className="absolute inset-0"
                      href={redirectUrl}
                      onClick={handleCardClick(product, index, 'keySpec')}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    ></a>
                    {variationDetails && variationDetails.length > 0 ? <CheckedYesIcon /> : <CheckedNoIcon />}
                  </td>
                )
              })}
            </tr>

            {keySpecRows}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default CompareTopPicks
